import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Eye, 
  MessageSquare, 
  BadgeCheck, 
  Users,
  Building2,
  Target,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  Globe,
  MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';

const DIVERSE_HANDS_IMAGE = "https://customer-assets.emergentagent.com/job_fa415626-c233-42d3-80fe-897a32ffd176/artifacts/tqytjsri_meritumpraxis%20professionals%20.png";

const features = [
  {
    icon: Search,
    title: 'Evidence-Based Discovery',
    description: 'Discover individuals based on verified evidence rather than informal networks.',
  },
  {
    icon: MapPin,
    title: 'Search by Country',
    description: 'Filter candidates by country to find skills in specific regions or globally.',
  },
  {
    icon: Filter,
    title: 'Advanced Search Filters',
    description: 'Search candidates using field, stage, verification status, evidence type, and institution.',
  },
  {
    icon: Eye,
    title: 'Profile Exploration',
    description: 'View detailed candidate profiles with verified achievements and portfolio links.',
  },
  {
    icon: MessageSquare,
    title: 'Direct Messaging',
    description: 'Initiate secure conversations with candidates directly through the platform.',
  },
  {
    icon: BadgeCheck,
    title: 'Verified Credentials',
    description: 'Access profiles where achievements are backed by referee validation.',
  },
];

const benefits = [
  {
    icon: Target,
    title: 'Efficient Skill Identification',
    description: 'Move beyond static CVs and identify skills based on verified, demonstrated capabilities.',
  },
  {
    icon: Users,
    title: 'Diverse Skill Pool',
    description: 'Access a wide range of candidates from various educational institutions and backgrounds.',
  },
  {
    icon: Building2,
    title: 'Quality Assurance',
    description: 'Every profile is structured and verified, ensuring quality and consistency.',
  },
];

export default function Organizations() {
  return (
    <div data-testid="organizations-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-sm text-meritum-gold font-body text-sm font-medium mb-6">
                For Organizations
              </span>
              <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight leading-none mb-6">
                Discover Skills
                <span className="block text-meritum-gold">Based on Evidence</span>
              </h1>
              <p className="font-body text-lg md:text-xl text-slate-200 leading-relaxed mb-8">
                Move beyond traditional CVs. Access verified professional profiles and identify 
                candidates whose demonstrated growth aligns with your organizational needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button 
                    data-testid="org-explore-skill-btn"
                    className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-6 text-base"
                  >
                    Start Exploring
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-meritum-navy font-body font-medium px-8 py-6 text-base"
                  >
                    Contact for Pricing
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src={DIVERSE_HANDS_IMAGE}
                alt="Diverse team collaboration"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-meritum-gold/10 text-meritum-gold font-body text-sm font-medium rounded-sm mb-4">
              Why MeritumPraxis?
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
              Transform Your Skill Discovery
            </h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
              Our platform offers unique advantages for organizations seeking evidence-based 
              skill identification.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="p-8 bg-slate-50 rounded-lg hover:bg-meritum-teal/5 transition-colors duration-300"
              >
                <div className="w-16 h-16 bg-meritum-teal/10 rounded-full flex items-center justify-center mb-6">
                  <benefit.icon className="text-meritum-teal" size={28} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                  {benefit.title}
                </h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-meritum-teal/10 text-meritum-teal font-body text-sm font-medium rounded-sm mb-4">
              Platform Features
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
              What You Can Do
            </h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
              Powerful tools designed for efficient and effective skill discovery.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-meritum-gold/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-meritum-gold/20 transition-colors duration-300">
                  <feature.icon className="text-meritum-gold" size={24} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                  {feature.title}
                </h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works for Organizations */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-meritum-teal/5 p-8 rounded-lg border border-meritum-teal/10">
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-6">
                  Search & Filter Example
                </h3>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <label className="font-body text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Country
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <Globe size={16} className="text-meritum-teal" />
                      <p className="font-body text-sm text-meritum-navy">South Africa, Nigeria, Kenya</p>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <label className="font-body text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Field
                    </label>
                    <p className="font-body text-sm text-meritum-navy">Software Engineering, Data Science</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <label className="font-body text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Stage
                    </label>
                    <p className="font-body text-sm text-meritum-navy">Final Year / Recent Graduate</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <label className="font-body text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Verification Status
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                        Verified Only
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-3 py-1 bg-meritum-gold/10 text-meritum-gold font-body text-sm font-medium rounded-sm mb-4">
                How It Works
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
                Find the Right Skills
              </h2>
              <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed mb-8">
                Use our comprehensive search and filtering tools to find candidates 
                who match your specific requirements.
              </p>
              <ul className="space-y-4">
                {[
                  'Search by country, field, and verification status',
                  'Filter by evidence type and institution',
                  'View detailed verified profiles with portfolio links',
                  'Initiate conversations directly',
                  'Track and manage candidate interactions',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-meritum-gold mt-0.5 flex-shrink-0" />
                    <span className="font-body text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 bg-meritum-gold/10 border-y border-meritum-gold/20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
              Important Note
            </h3>
            <p className="font-body text-slate-600 leading-relaxed">
              MeritumPraxis is not a recruitment agency. The platform does not recruit candidates 
              on behalf of organizations, negotiate employment terms, or represent candidates in 
              hiring discussions. We only provide a platform that enables professional visibility 
              and discovery.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
              Custom Pricing for Organizations
            </h3>
            <p className="font-body text-slate-600 leading-relaxed mb-6">
              Organization pricing is customized based on your needs. Contact us to receive 
              detailed pricing information via email.
            </p>
            <Link to="/contact">
              <Button className="bg-meritum-teal text-white hover:bg-meritum-navy font-body font-bold px-8 py-4">
                Contact for Pricing
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-meritum-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
            Ready to Transform Your Hiring?
          </h2>
          <p className="font-body text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            Join forward-thinking organizations using evidence-based skill discovery. 
            Contact us to learn more or start exploring skills today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button 
                data-testid="org-cta-explore-btn"
                className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-6 text-base"
              >
                Start Exploring Skills
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-meritum-navy font-body font-medium px-8 py-6 text-base"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
